import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import ShiftPlanCalendar from './ShiftPlanCalendar';
import PlanSwapRequestModal from './PlanSwapRequestModal';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';
import { doc, getDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const SubmitShiftPlanPage = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { submitPlanRequest, getPlanById, getPlanRequests } = useShiftPlan();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [existingRequest, setExistingRequest] = useState(null);
  const [teamMembersWithProfile, setTeamMembersWithProfile] = useState([]);
  const [otherMembersShifts, setOtherMembersShifts] = useState([]); // Shifts submitted by other members
  const [selectedMembers, setSelectedMembers] = useState([]); // Selected members to filter
  const [showMemberFilter, setShowMemberFilter] = useState(false); // Show/hide filter modal
  const [showSwapModal, setShowSwapModal] = useState(false); // Show/hide swap request modal
  const [swapModalData, setSwapModalData] = useState(null); // Data for swap modal
  const [pendingSwapRequests, setPendingSwapRequests] = useState([]); // Swap requests for this plan

  // Fetch team members with profile data
  useEffect(() => {
    const fetchTeamMembersWithProfile = async () => {
      if (!currentTeam || !currentTeam.members) return;

      try {
        const membersWithProfile = await Promise.all(
          currentTeam.members.map(async (member) => {
            try {
              const userRef = doc(db, 'users', member.uid);
              const userSnap = await getDoc(userRef);
              const userData = userSnap.exists() ? userSnap.data() : null;
              
              return {
                ...member,
                photoURL: userData?.photoURL || null,
                displayName: userData?.displayName || member.name || 'Unknown'
              };
            } catch (err) {
              console.error(`Error fetching user ${member.uid}:`, err);
              return {
                ...member,
                photoURL: null,
                displayName: member.name || 'Unknown'
              };
            }
          })
        );
        setTeamMembersWithProfile(membersWithProfile);
        
        // Initialize selectedMembers with all members (default: show all)
        if (membersWithProfile.length > 0) {
          setSelectedMembers(membersWithProfile.map(m => m.uid));
        }
      } catch (err) {
        console.error('Error fetching team members:', err);
      }
    };

    if (currentTeam) {
      fetchTeamMembersWithProfile();
    }
  }, [currentTeam]);

  // Load plan data and existing request
  useEffect(() => {
    const loadPlan = async () => {
      try {
        setLoading(true);
        const result = await getPlanById(planId);
        
        if (result.success) {
          setPlan(result.plan);
          
          // Load all requests to show other members' shifts
          if (currentUser?.uid) {
            const requests = await getPlanRequests(planId);
            
            // Find user's own request
            const userRequest = requests.find(
              req => req.userId === currentUser.uid
            );
            
            if (userRequest && userRequest.requestedShifts) {
              setExistingRequest(userRequest);
              setSelectedShifts(userRequest.requestedShifts);
            }
            
            // Get other members' submitted shifts (for display only)
            const otherShifts = [];
            requests.forEach(request => {
              if (request.userId !== currentUser.uid && 
                  (request.status === 'SUBMITTED' || request.status === 'APPROVED') &&
                  request.requestedShifts) {
                request.requestedShifts.forEach(shift => {
                  otherShifts.push({
                    ...shift,
                    userId: request.userId,
                    userName: request.userName
                  });
                });
              }
            });
            setOtherMembersShifts(otherShifts);
          }
        } else {
          setError(result.error || 'ไม่พบแผน');
        }
      } catch (err) {
        console.error('Error loading plan:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (planId && currentUser) {
      loadPlan();
    }
  }, [planId, currentUser, getPlanById, getPlanRequests]);

  // Fetch pending swap requests for this plan (to show in calendar)
  useEffect(() => {
    if (!planId || !currentTeam) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'shiftSwaps'),
        where('planId', '==', planId),
        where('status', '==', 'PENDING')
      ),
      (snapshot) => {
        const swaps = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPendingSwapRequests(swaps);
      },
      (err) => {
        console.error('Error fetching swap requests:', err);
      }
    );

    return () => unsubscribe();
  }, [planId, currentTeam]);

  // Filter other members' shifts based on selected members
  const filteredOtherMembersShifts = useMemo(() => {
    if (selectedMembers.length === 0 || selectedMembers.length === teamMembersWithProfile.length) {
      // Show all if no filter or all selected
      return otherMembersShifts;
    }
    return otherMembersShifts.filter(shift => selectedMembers.includes(shift.userId));
  }, [otherMembersShifts, selectedMembers, teamMembersWithProfile.length]);

  // Toggle member selection
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        const newSelection = prev.filter(id => id !== memberId);
        // Don't allow deselecting all members
        return newSelection.length > 0 ? newSelection : prev;
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Select all members
  const selectAllMembers = () => {
    setSelectedMembers(teamMembersWithProfile.map(m => m.uid));
  };

  // Deselect all members (but keep at least one)
  const deselectAllMembers = () => {
    if (currentUser?.uid) {
      // Keep only current user
      setSelectedMembers([currentUser.uid]);
    }
  };

  // Handle shift selection
  const handleShiftSelect = (shift) => {
    setSelectedShifts(prev => {
      // Check if shift for this date already exists
      const existing = prev.find(s => s.date === shift.date && s.shiftType === shift.shiftType);
      if (existing) return prev;
      
      return [...prev, shift];
    });
  };

  // Handle shift removal
  const handleShiftRemove = (shiftToRemove) => {
    setSelectedShifts(prev => 
      prev.filter(shift => 
        !(shift.date === shiftToRemove.date && shift.shiftType === shiftToRemove.shiftType)
      )
    );
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (selectedShifts.length === 0) {
        throw new Error('กรุณาเลือกเวรอย่างน้อย 1 วัน');
      }

      const result = await submitPlanRequest(planId, selectedShifts);

      if (result.success) {
        alert('ส่งคำขอเวรเรียบร้อยแล้ว!');
        navigate('/nurse/shifts');
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการส่งคำขอ');
      }
    } catch (err) {
      console.error('Error submitting plan request:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการส่งคำขอ');
    } finally {
      setSubmitting(false);
    }
  };

  // Get shift type label
  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  // Count shifts by type
  const shiftsByType = selectedShifts.reduce((acc, shift) => {
    acc[shift.shiftType] = (acc[shift.shiftType] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">ไม่พบแผน</p>
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าจัดการเวร
          </button>
        </div>
      </div>
    );
  }

  // Check if plan is in DRAFT or SUBMITTED status (can submit before approve)
  if (plan.status !== 'DRAFT' && plan.status !== 'SUBMITTED') {
    const isApproved = plan.status === 'APPROVED';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">{isApproved ? '✅' : '⚠️'}</div>
          <p className="text-gray-600 mb-2 text-lg font-medium">
            {isApproved ? 'แผนนี้อนุมัติแล้ว' : 'แผนนี้ไม่สามารถกรอกได้แล้ว'}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {isApproved 
              ? 'แผนที่อนุมัติแล้วไม่สามารถแก้ไขได้ กรุณาดูแผนแทน' 
              : `สถานะ: ${plan.status}`}
          </p>
          <div className="flex gap-3 justify-center">
            {isApproved && (
              <button
                onClick={() => navigate(`/nurse/shifts/plans/${planId}/review`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                ดูแผน
              </button>
            )}
            <button
              onClick={() => navigate('/nurse/shifts/plans')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              กลับไปหน้าแผนเวร
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is a team member
  const isTeamMember = currentTeam?.members?.some(
    member => member.uid === currentUser?.uid
  ) || currentTeam?.leaderId === currentUser?.uid;

  if (!isTeamMember) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-2">คุณไม่ใช่สมาชิกของทีมนี้</p>
          <button
            onClick={() => navigate('/nurse/shifts/plans')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าแผนเวร
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับไปหน้าจัดการเวร
          </button>
          <h1 className="text-2xl font-bold text-gray-800">กรอกเวร</h1>
          <p className="text-sm text-gray-600 mt-1">
            แผน: <span className="font-medium">{plan.planNumber}</span> (Version {plan.version})
          </p>
        </div>

        {/* Plan Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">เลขแผน</p>
              <p className="font-medium text-gray-800">{plan.planNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Version</p>
              <p className="font-medium text-gray-800">{plan.version}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">สถานะ</p>
              <p className="font-medium text-gray-800">{plan.status}</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Existing Request Notice */}
        {existingRequest && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">คุณได้ส่งคำขอเวรแล้ว</p>
            <p className="text-sm mt-1">คุณสามารถแก้ไขและส่งใหม่ได้</p>
          </div>
        )}

        {/* Calendar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">เลือกเวรที่ต้องการ</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/nurse/shifts/swaps/plan-requests')}
                className="px-3 py-1.5 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition flex items-center gap-2 border border-orange-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                คำขอแลกเวร
                {pendingSwapRequests.filter(s => s.requestedTo === currentUser?.uid && s.status === 'PENDING').length > 0 && (
                  <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {pendingSwapRequests.filter(s => s.requestedTo === currentUser?.uid && s.status === 'PENDING').length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowMemberFilter(true)}
                className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition flex items-center gap-2 border border-blue-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                กรองสมาชิก
                {selectedMembers.length < teamMembersWithProfile.length && (
                  <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {selectedMembers.length}
                  </span>
                )}
              </button>
            </div>
          </div>
          <ShiftPlanCalendar
            plan={plan}
            selectedShifts={selectedShifts}
            onShiftSelect={handleShiftSelect}
            onShiftRemove={handleShiftRemove}
            readOnly={false}
            teamMembersWithProfile={teamMembersWithProfile}
            otherMembersShifts={filteredOtherMembersShifts}
            pendingSwapRequests={pendingSwapRequests}
            onSwapRequest={(date, userShifts, otherShifts) => {
              setSwapModalData({ date, userShifts, otherShifts });
              setShowSwapModal(true);
            }}
          />
        </div>
        
        {/* Member Filter Modal */}
        {showMemberFilter && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">กรองตามสมาชิก</h3>
                <button
                  onClick={() => setShowMemberFilter(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4 flex gap-2">
                <button
                  onClick={selectAllMembers}
                  className="flex-1 px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition border border-green-200"
                >
                  เลือกทั้งหมด
                </button>
                <button
                  onClick={deselectAllMembers}
                  className="flex-1 px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition border border-gray-200"
                >
                  เลือกเฉพาะฉัน
                </button>
              </div>
              
              <div className="space-y-2">
                {teamMembersWithProfile.map((member) => {
                  const isSelected = selectedMembers.includes(member.uid);
                  const isCurrentUser = member.uid === currentUser?.uid;
                  
                  return (
                    <button
                      key={member.uid}
                      onClick={() => toggleMemberSelection(member.uid)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition ${
                        isSelected
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'ring-2 ring-blue-500' : ''
                      }`}>
                        {member.photoURL ? (
                          <img
                            src={member.photoURL}
                            alt={member.displayName}
                            className="w-full h-full rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-full h-full rounded-full bg-gray-300 flex items-center justify-center text-white text-sm font-semibold ${
                            member.photoURL ? 'hidden' : 'flex'
                          }`}
                        >
                          {member.displayName?.charAt(0) || 'U'}
                        </div>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-800">
                          {member.displayName}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-blue-600">(คุณ)</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {otherMembersShifts.filter(s => s.userId === member.uid).length} เวร
                        </p>
                      </div>
                      {isSelected && (
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowMemberFilter(false)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  ตกลง
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Selected Shifts Summary */}
        {selectedShifts.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">สรุปเวรที่เลือก</h2>
            
            {/* Summary by type */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">
                  {shiftsByType[SHIFT_TYPES.MORNING] || 0}
                </p>
                <p className="text-sm text-green-600">เวรเช้า</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-700">
                  {shiftsByType[SHIFT_TYPES.AFTERNOON] || 0}
                </p>
                <p className="text-sm text-yellow-600">เวรบ่าย</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">
                  {shiftsByType[SHIFT_TYPES.NIGHT] || 0}
                </p>
                <p className="text-sm text-blue-600">เวรดึก</p>
              </div>
            </div>

            {/* Total */}
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-semibold text-gray-800">
                รวมทั้งหมด: {selectedShifts.length} วัน
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/nurse/shifts')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            ยกเลิก
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selectedShifts.length === 0}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'กำลังส่ง...' : 'ส่งคำขอ'}
          </button>
        </div>
        
        {/* Plan Swap Request Modal */}
        {showSwapModal && swapModalData && (
          <PlanSwapRequestModal
            isOpen={showSwapModal}
            onClose={() => {
              setShowSwapModal(false);
              setSwapModalData(null);
            }}
            planId={planId}
            plan={plan}
            date={swapModalData.date}
            userShifts={swapModalData.userShifts}
            otherShifts={swapModalData.otherShifts}
            teamMembersWithProfile={teamMembersWithProfile}
          />
        )}
      </div>
    </div>
  );
};

export default SubmitShiftPlanPage;

